import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { CheckCircle2, Clock, XCircle, MinusCircle, ChevronDown, ChevronRight, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Skeleton } from "../components/ui/skeleton";
import { useAuth, API } from "../App";
import { extractError } from "../lib/errors";
import { toast } from "sonner";

const FlaskIcon = ({ className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 3h6M9 3v7l-4 9h14l-4-9V3"/>
  </svg>
);

const STATUS_META = {
  compliant:      { label: "Conforme",    icon: CheckCircle2, color: "text-emerald-500", badge: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" },
  partial:        { label: "Parcial",     icon: Clock,        color: "text-amber-500",   badge: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
  non_compliant:  { label: "No Conforme", icon: XCircle,      color: "text-red-500",     badge: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
  not_applicable: { label: "No Aplica",   icon: MinusCircle,  color: "text-slate-400",   badge: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400" },
};

const PROFILES = [
  { key: "alimentos",    label: "Alimentos",     color: "#f59e0b", authority: "SENASA / PROCONSUMIDOR" },
  { key: "cosmeticos",   label: "Cosméticos",    color: "#8b5cf6", authority: "SESPAS" },
  { key: "farmaceuticos",label: "Farmacéuticos", color: "#ef4444", authority: "SESPAS / FDA" },
];

const ItemRow = ({ item, response, onSave, onSuggest }) => {
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState(response?.status || "");
  const [notes, setNotes] = useState(response?.notes || "");
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(item.id, { status, notes });
    setSaving(false);
  };

  const handleSuggest = async () => {
    setAiLoading(true);
    const text = await onSuggest(item);
    if (text) setNotes((prev) => (prev ? `${prev}\n\n${text}` : text));
    setAiLoading(false);
  };

  const meta = STATUS_META[status];

  return (
    <div className="border border-border rounded-lg">
      <button
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-muted/50 rounded-lg transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="secondary" className="text-[10px] flex-shrink-0">{item.section}</Badge>
            <span className="text-sm font-medium truncate">{item.title}</span>
            {meta && (
              <Badge variant="outline" className={`text-[10px] ${meta.badge} flex-shrink-0`}>
                <meta.icon className="w-2.5 h-2.5 mr-1" />
                {meta.label}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{item.description}</p>
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
          <p className="text-sm text-muted-foreground">{item.description}</p>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-8 text-sm w-56">
              <SelectValue placeholder="Estado..." />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_META).map(([val, m]) => (
                <SelectItem key={val} value={val}>
                  <div className="flex items-center gap-2">
                    <m.icon className={`w-3.5 h-3.5 ${m.color}`} />
                    {m.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            placeholder="Notas o referencia de evidencia..."
            className="text-sm h-20 resize-none"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={!status || saving}>
              {saving ? "Guardando..." : "Guardar"}
            </Button>
            <Button size="sm" variant="outline" onClick={handleSuggest} disabled={aiLoading}>
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />
              {aiLoading ? "Generando..." : "Sugerir con AI"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const ProfileTab = ({ profile, items, responses, onSave, onSuggest, loading }) => {
  const answered = items.filter((i) => responses[i.id]?.status).length;
  const compliant = items.filter((i) => responses[i.id]?.status === "compliant").length;
  const pct = items.length ? Math.round((answered / items.length) * 100) : 0;

  // Group by section
  const sections = [...new Set(items.map((i) => i.section))];

  return (
    <div className="space-y-4 mt-4">
      <Card><CardContent className="p-4">
        <div className="flex justify-between text-sm mb-1">
          <span>Progreso BPM {profile.label}</span>
          <span className="font-medium" style={{ color: profile.color }}>{pct}%</span>
        </div>
        <Progress value={pct} className="h-2" />
        <p className="text-xs text-muted-foreground mt-1">{compliant} conformes · {answered}/{items.length} evaluados · Autoridad: {profile.authority}</p>
      </CardContent></Card>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      ) : (
        sections.map((sec) => {
          const secItems = items.filter((i) => i.section === sec);
          return (
            <Card key={sec}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{sec}</CardTitle>
                <CardDescription className="text-xs">{secItems.length} ítems</CardDescription>
              </CardHeader>
              <CardContent className="pt-0 space-y-2">
                {secItems.map((item) => (
                  <ItemRow key={item.id} item={item} response={responses[item.id]} onSave={onSave} onSuggest={onSuggest} />
                ))}
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
};

const ZonaFrancaBPM = () => {
  const { token } = useAuth();
  const [activeProfile, setActiveProfile] = useState("alimentos");
  const [allItems, setAllItems] = useState({});
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const ctrlRes = await axios.get(`${API}/zona-franca/controls/bpm`);
      setAllItems(ctrlRes.data.controls || {});
    } catch {
      toast.error("No se pudo cargar los ítems BPM");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProfile = useCallback(async (profile) => {
    try {
      const res = await axios.get(`${API}/zona-franca/bpm/checklist?profile=${profile}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResponses((prev) => ({ ...prev, [profile]: res.data.responses || {} }));
    } catch {}
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadProfile(activeProfile); }, [activeProfile, loadProfile]);

  const handleSave = async (itemId, body) => {
    try {
      const res = await axios.put(
        `${API}/zona-franca/bpm/checklist/${itemId}?profile=${activeProfile}`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResponses((prev) => ({
        ...prev,
        [activeProfile]: { ...(prev[activeProfile] || {}), [itemId]: body },
      }));
      toast.success(`Ítem actualizado — ${res.data.completion_pct}%`);
    } catch {
      toast.error("Error al guardar");
    }
  };

  const handleSuggest = async (item) => {
    try {
      const res = await axios.post(
        `${API}/zona-franca/ai/suggest`,
        {
          framework_code: "bpm",
          control_id: item.id,
          control_title: `${item.section} — ${item.title}`,
          control_description: item.description,
          profile: activeProfile,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Sugerencia generada");
      return res.data.suggestion;
    } catch (err) {
      toast.error(extractError(err, "No se pudo generar sugerencia"));
      return "";
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: "#8b5cf620", border: "1px solid #8b5cf640" }}>
            <FlaskIcon className="w-5 h-5 text-purple-500" />
          </div>
          <div>
            <h1 className="text-xl font-heading font-bold">BPM — Buenas Prácticas de Manufactura</h1>
            <p className="text-sm text-muted-foreground">3 perfiles: Alimentos · Cosméticos · Farmacéuticos</p>
          </div>
        </div>
        <Badge variant="outline" className="text-xs">SENASA / SESPAS / OPS-OMS</Badge>
      </div>

      <Tabs value={activeProfile} onValueChange={setActiveProfile}>
        <TabsList className="grid w-full grid-cols-3">
          {PROFILES.map((p) => (
            <TabsTrigger key={p.key} value={p.key}>{p.label}</TabsTrigger>
          ))}
        </TabsList>
        {PROFILES.map((p) => (
          <TabsContent key={p.key} value={p.key}>
            <ProfileTab
              profile={p}
              items={allItems[p.key] || []}
              responses={responses[p.key] || {}}
              onSave={handleSave}
              onSuggest={handleSuggest}
              loading={loading}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default ZonaFrancaBPM;
